import { mod002, AdditivePartialBank } from '../../packages/mod-002/src/index.js';
import { mod001 } from '../../packages/mod-001/src/index.js';
import { mod003 } from '../../packages/mod-003/src/index.js';
import { mod004 } from '../../packages/mod-004/src/index.js';
import { mod005 } from '../../packages/mod-005/src/index.js';
import { mod006 } from '../../packages/mod-006/src/index.js';
import { mod007, SelfBufferBank } from '../../packages/mod-007/src/index.js';
import { mod008 } from '../../packages/mod-008/src/index.js';
import { mod009 } from '../../packages/mod-009/src/index.js';
import { mod010, StringWaveguideResonator } from '../../packages/mod-010/src/index.js';
import { mod011, TubeReedLipResonator } from '../../packages/mod-011/src/index.js';
import { mod012, FrictionBowExciter } from '../../packages/mod-012/src/index.js';
import { mod013, GranularEngine } from '../../packages/mod-013/src/index.js';
import { mod014 } from '../../packages/mod-014/src/index.js';
import { mod015 } from '../../packages/mod-015/src/index.js';
import { mod016 } from '../../packages/mod-016/src/index.js';
import { mod018 } from '../../packages/mod-018/src/index.js';
import { mod019 } from '../../packages/mod-019/src/index.js';
import { mod020 } from '../../packages/mod-020/src/index.js';
import { mod021 } from '../../packages/mod-021/src/index.js';
import { mod022, CombFilter } from '../../packages/mod-022/src/index.js';
import { mod023 } from '../../packages/mod-023/src/index.js';
import { mod024 } from '../../packages/mod-024/src/index.js';
import { mod025 } from '../../packages/mod-025/src/index.js';
import { mod026 } from '../../packages/mod-026/src/index.js';
import { mod027, ResamplerBank } from '../../packages/mod-027/src/index.js';
import { mod028 } from '../../packages/mod-028/src/index.js';
import { mod029 } from '../../packages/mod-029/src/index.js';
import { mod030 } from '../../packages/mod-030/src/index.js';
import { mod031 } from '../../packages/mod-031/src/index.js';
import { mod031v2 } from '../../packages/mod-031/src/v2.js';
import { mod032, ConvolutionBodyIrProcessor, generateProceduralIr } from '../../packages/mod-032/src/index.js';
import { mod033, SpatializerProcessor } from '../../packages/mod-033/src/index.js';
import { mod034, MixerRoutingGraph } from '../../packages/mod-034/src/index.js';
import { mod035 } from '../../packages/mod-035/src/index.js';
import { mod036, renderOffline } from '../../packages/mod-036/src/index.js';
import { ModulationMatrix, mod017Descriptor } from '../../packages/mod-017/src/index.js';
import { ModuleRegistry } from '../../packages/module-runtime/src/index.js';
import type { PatchGraphV1 } from '../../packages/routing-graph/src/index.js';
import { ALLMOST_DEFAULT_RUNTIME, createAllmostDefaultPatch, createDefaultRuntimeModules } from './default-routing.js';

export const allmostModules = Object.freeze([
  mod001, mod002, mod003, mod004, mod005, mod006, mod007, mod008, mod009, mod010, mod011, mod012, mod013, mod014, mod015, mod016, mod018, mod019, mod020,
  mod021, mod022, mod023, mod024, mod025, mod026, mod027, mod028, mod029, mod030, mod031, mod032, mod033, mod034, mod035, mod036,
]);

/** Complete visual catalog. MOD-017 is metadata-only because its runtime is a
 * host-level control router, not a block-processing module definition. */
export const allmostDescriptors = Object.freeze([
  ...allmostModules.slice(0, 16).map(({ descriptor }) => descriptor),
  mod017Descriptor,
  ...allmostModules.slice(16).map(({ descriptor }) => descriptor),
]);

export const allmostGraphModules = Object.freeze([...allmostModules, mod031v2]);
export const allmostGraphDescriptors = Object.freeze([...allmostDescriptors, mod031v2.descriptor]);
export const allmostPatch: PatchGraphV1 = createAllmostDefaultPatch(allmostDescriptors);
export const allmostDefaultRuntimeModules = createDefaultRuntimeModules(allmostDescriptors);
export { ALLMOST_DEFAULT_RUNTIME };

export interface OfflineRendererQaReadback {
  readonly route: 'mir.mod-036';
  readonly sampleRate: number;
  readonly channels: number;
  readonly frames: number;
  readonly callbackCount: number;
  readonly wavBytes: number;
  readonly contentHash: string;
  readonly peak: number;
  readonly rms: number;
  readonly truePeak4x: number;
  readonly finite: boolean;
  readonly nonzero: boolean;
  readonly audible: boolean;
  readonly bounded: boolean;
}

export const runOfflineRendererQaRoute = (sampleRate = 48_000, frames = 4_800): OfflineRendererQaReadback => {
  const result = renderOffline({
    sampleRate,
    channels: 2,
    sourceFrames: frames,
    blockSize: 128,
    metadata: { INAM: 'allmost MOD-036 deterministic QA' },
    renderBlock: ({ startFrame, frames: blockFrames, outputs }) => {
      for (let offset = 0; offset < blockFrames; offset += 1) {
        const frame = startFrame + offset;
        outputs[0]![offset] = Math.sin((2 * Math.PI * 220 * frame) / sampleRate) * 0.2;
        outputs[1]![offset] = Math.sin((2 * Math.PI * 330 * frame) / sampleRate) * 0.16;
      }
    },
  });
  const aggregate = result.analysis.aggregate;
  return Object.freeze({
    route: 'mir.mod-036', sampleRate: result.sampleRate, channels: result.channels, frames: result.totalFrames,
    callbackCount: result.callbackCount, wavBytes: result.wavBytes.byteLength, contentHash: result.contentHash,
    peak: aggregate.absolutePeak, rms: aggregate.rms, truePeak4x: aggregate.truePeak4x,
    finite: aggregate.nonFiniteCount === 0, nonzero: aggregate.absolutePeak > 1e-6,
    audible: aggregate.rms > 0.01,
    bounded: aggregate.absolutePeak <= 1 && aggregate.truePeak4x <= 1,
  });
};

export type ModuleLabFilterMode = 'low-pass' | 'high-pass' | 'band-pass' | 'notch' | 'all-pass';
export interface ModuleLabOptions {
  readonly lfoRateHz?: number;
  readonly modulationDepthSemitones?: number;
  readonly filterCutoffHz?: number;
  readonly filterQ?: number;
  readonly filterMode?: ModuleLabFilterMode;
}

export interface AudioSmokeResult {
  readonly patchId: string;
  readonly moduleIds: readonly string[];
  readonly audibleRoute: typeof ALLMOST_DEFAULT_RUNTIME.activeAudioRoute;
  readonly modulationSource: 'mir.mod-015';
  readonly frames: number;
  readonly finite: boolean;
  readonly peak: number;
  readonly rms: number;
  readonly samples: Float32Array;
}

const FILTER_MODE_INDEX: Readonly<Record<ModuleLabFilterMode, number>> = Object.freeze({
  'low-pass': 0,
  'high-pass': 1,
  'band-pass': 2,
  notch: 3,
  'all-pass': 4,
});

const bounded = (label: string, value: number, minimum: number, maximum: number): number => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
  return value;
};

/**
 * Presentation renderer using the real package adapters. MOD-015 is translated
 * by the host into a sample-accurate MIDI-note control signal for MOD-019 key
 * tracking. The fixed backend executes the same safe audible route declared by
 * ALLMOST_DEFAULT_RUNTIME; visual Patch Bay edits do not recompile it yet.
 */
export const runModuleLabAudio = (
  sampleRate = 48_000,
  frames = 4096,
  options: ModuleLabOptions = {},
): AudioSmokeResult => {
  if (!Number.isFinite(sampleRate) || sampleRate < 8_000 || sampleRate > 192_000) throw new RangeError('invalid sampleRate');
  if (!Number.isInteger(frames) || frames < 1) throw new RangeError('frames must be a positive integer');
  const lfoRateHz = bounded('lfoRateHz', options.lfoRateHz ?? 0.5, 0.01, 20);
  const depth = bounded('modulationDepthSemitones', options.modulationDepthSemitones ?? 24, 0, 48);
  const cutoff = bounded('filterCutoffHz', options.filterCutoffHz ?? 1_200, 20, Math.min(20_000, sampleRate * 0.45));
  const q = bounded('filterQ', options.filterQ ?? 1.2, 0.25, 20);
  const mode = options.filterMode ?? 'low-pass';
  if (FILTER_MODE_INDEX[mode] === undefined) throw new RangeError('filterMode is invalid');

  const maxBlockSize = Math.min(frames, 8_192);
  const context = { sampleRate, maxBlockSize };
  const oscillator = mod001.createInstance(context);
  const lfo = mod015.createInstance(context);
  const filter = mod019.createInstance(context);
  const equalizer = mod021.createInstance(context);
  const mixer = mod034.createInstance(context);
  const safety = mod025.createInstance(context);
  oscillator.setParameter('frequency', 110);
  oscillator.setParameter('waveform', 2);
  lfo.setParameter('waveform', 0);
  lfo.setParameter('rate-mode', 0);
  lfo.setParameter('rate-hz', lfoRateHz);
  lfo.setParameter('polarity', 0);
  filter.setParameter('mode', FILTER_MODE_INDEX[mode]);
  filter.setParameter('cutoff-hz', cutoff);
  filter.setParameter('q', q);
  filter.setParameter('key-track', 1);
  safety.setParameter('threshold-db', -18);
  safety.setParameter('ratio', 4);
  safety.setParameter('lookahead-ms', 5);
  safety.setParameter('limiter-ceiling-db', -1);
  safety.setParameter('soft-clip-drive', 1);

  const oscillatorOutput = new Float32Array(frames);
  const lfoOutput = new Float32Array(frames);
  const noteControl = new Float32Array(frames);
  const filtered = new Float32Array(frames);
  const equalized = new Float32Array(frames);
  const mixed = new Float32Array(frames);
  const safe = new Float32Array(frames);
  for (let offset = 0; offset < frames; offset += maxBlockSize) {
    const blockFrames = Math.min(maxBlockSize, frames - offset);
    const end = offset + blockFrames;
    const oscillatorBlock = oscillatorOutput.subarray(offset, end);
    const lfoBlock = lfoOutput.subarray(offset, end);
    const noteBlock = noteControl.subarray(offset, end);
    const filterBlock = filtered.subarray(offset, end);
    const equalizerBlock = equalized.subarray(offset, end);
    const mixerBlock = mixed.subarray(offset, end);
    const safetyBlock = safe.subarray(offset, end);
    oscillator.process({ frames: blockFrames, inputs: {}, outputs: { out: oscillatorBlock }, controls: {} });
    lfo.process({ frames: blockFrames, inputs: {}, outputs: { out: lfoBlock }, controls: {} });
    for (let index = 0; index < blockFrames; index += 1) noteBlock[index] = 69 + lfoBlock[index]! * depth;
    filter.process({ frames: blockFrames, inputs: { in: oscillatorBlock }, outputs: { out: filterBlock }, controls: { note: noteBlock } });
    equalizer.process({ frames: blockFrames, inputs: { in: filterBlock }, outputs: { out: equalizerBlock }, controls: {} });
    mixer.process({ frames: blockFrames, inputs: { in: equalizerBlock }, outputs: { out: mixerBlock }, controls: {} });
    safety.process({ frames: blockFrames, inputs: { in: mixerBlock }, outputs: { out: safetyBlock }, controls: {} });
  }

  const samples = new Float32Array(frames);
  let peak = 0;
  let sumSquares = 0;
  let finite = true;
  for (let index = 0; index < frames; index += 1) {
    const sample = safe[index]! * 0.6;
    samples[index] = sample;
    finite &&= Number.isFinite(sample);
    peak = Math.max(peak, Math.abs(sample));
    sumSquares += sample * sample;
  }
  const registry = new ModuleRegistry(allmostModules);
  return Object.freeze({
    patchId: allmostPatch.id,
    moduleIds: registry.moduleIds(),
    audibleRoute: ALLMOST_DEFAULT_RUNTIME.activeAudioRoute,
    modulationSource: 'mir.mod-015',
    frames,
    finite,
    peak,
    rms: Math.sqrt(sumSquares / frames),
    samples,
  });
};

export const runOfflineAudioSmoke = runModuleLabAudio;

export interface ReferenceChainOptions {
  readonly drive?: number;
  readonly thresholdDb?: number;
}

export interface ReferenceChainResult {
  readonly audibleRoute: readonly ['mir.mod-001', 'mir.mod-008', 'mir.mod-021', 'mir.mod-023', 'mir.mod-025'];
  readonly frames: number;
  readonly finite: boolean;
  readonly peak: number;
  readonly rms: number;
  readonly samples: Float32Array;
}

/** Audible, deterministic reference route for the independently delivered DSP lanes. */
export const runReferenceChainAudio = (
  sampleRate = 48_000,
  frames = 48_000,
  options: ReferenceChainOptions = {},
): ReferenceChainResult => {
  if (!Number.isFinite(sampleRate) || sampleRate < 8_000 || sampleRate > 192_000) throw new RangeError('invalid sampleRate');
  if (!Number.isInteger(frames) || frames < 1) throw new RangeError('frames must be a positive integer');
  const drive = bounded('drive', options.drive ?? 2, 0.25, 12);
  const thresholdDb = bounded('thresholdDb', options.thresholdDb ?? -18, -48, 0);
  const context = { sampleRate, maxBlockSize: frames };
  const oscillator = mod001.createInstance(context);
  const resonator = mod008.createInstance(context);
  const equalizer = mod021.createInstance(context);
  const waveshaper = mod023.createInstance(context);
  const dynamics = mod025.createInstance(context);

  oscillator.setParameter('frequency', 82.41);
  oscillator.setParameter('waveform', 2);
  resonator.setParameter('decay-scale', 0.6);
  resonator.setParameter('output-gain', 0.3);
  equalizer.setParameter('section-0-type', 1);
  equalizer.setParameter('section-0-frequency-hz', 160);
  equalizer.setParameter('section-0-q', 0.707);
  waveshaper.setParameter('mode', 1);
  waveshaper.setParameter('drive', drive);
  waveshaper.setParameter('mix', 0.65);
  waveshaper.setParameter('output', 0.7);
  dynamics.setParameter('threshold-db', thresholdDb);
  dynamics.setParameter('ratio', 6);
  dynamics.setParameter('lookahead-ms', 2);
  dynamics.setParameter('limiter-ceiling-db', -1);
  dynamics.setParameter('soft-clip-drive', 1.25);

  const source = new Float32Array(frames);
  const left = new Float32Array(frames);
  const right = new Float32Array(frames);
  const resonated = new Float32Array(frames);
  const equalized = new Float32Array(frames);
  const shaped = new Float32Array(frames);
  const samples = new Float32Array(frames);
  oscillator.process({ frames, inputs: {}, outputs: { out: source }, controls: {} });
  resonator.process({ frames, inputs: { excitation: source }, outputs: { left, right }, controls: {} });
  for (let index = 0; index < frames; index += 1) resonated[index] = (left[index]! + right[index]!) * 0.5;
  equalizer.process({ frames, inputs: { in: resonated }, outputs: { out: equalized }, controls: {} });
  waveshaper.process({ frames, inputs: { in: equalized }, outputs: { out: shaped }, controls: {} });
  dynamics.process({ frames, inputs: { in: shaped }, outputs: { out: samples }, controls: {} });

  let peak = 0;
  let sumSquares = 0;
  let finite = true;
  for (const sample of samples) {
    finite &&= Number.isFinite(sample);
    peak = Math.max(peak, Math.abs(sample));
    sumSquares += sample * sample;
  }
  return Object.freeze({
    audibleRoute: Object.freeze(['mir.mod-001', 'mir.mod-008', 'mir.mod-021', 'mir.mod-023', 'mir.mod-025'] as const),
    frames,
    finite,
    peak,
    rms: Math.sqrt(sumSquares / frames),
    samples,
  });
};

export interface PercussionReferenceResult {
  readonly audibleRoute: readonly ['mir.mod-009', 'mir.mod-024'];
  readonly controlRoute: readonly ['mir.mod-017', 'mir.mod-009'];
  readonly frames: number;
  readonly finite: boolean;
  readonly peak: number;
  readonly rms: number;
  readonly samples: Float32Array;
}

/** Bounded drum route: MOD-017 accents MOD-009 excitation energy before MOD-024 articulation. */
export const runPercussionReferenceAudio = (
  sampleRate = 48_000,
  frames = 48_000,
): PercussionReferenceResult => {
  if (!Number.isFinite(sampleRate) || sampleRate < 8_000 || sampleRate > 192_000) throw new RangeError('invalid sampleRate');
  if (!Number.isInteger(frames) || frames < 1) throw new RangeError('frames must be a positive integer');

  const accent = new Float32Array(frames);
  accent.fill(-1);
  const excitation = new Float32Array(frames);
  const interval = Math.max(1, Math.round(sampleRate * 0.25));
  for (let frame = 0; frame < frames; frame += interval) {
    accent[frame] = 1;
    excitation[frame] = 0.8;
  }
  const energy = new Float32Array(frames);
  const matrix = new ModulationMatrix({
    sampleRate,
    sources: [{ id: 'accent', kind: 'control' }],
    targets: [{ id: 'energy', kind: 'parameter', minimum: 0.2, maximum: 1, baseValue: 0.55 }],
    routes: [{ id: 'accent-to-energy', source: 'accent', target: 'energy', depth: 0.35, transform: 'unipolar' }],
  });
  matrix.processInto({ accent }, { energy });

  const context = { sampleRate, maxBlockSize: frames };
  const drum = mod009.createInstance(context);
  drum.setParameter('body', 0);
  drum.setParameter('fundamental-hz', 58);
  drum.setParameter('decay-seconds', 0.55);
  drum.setParameter('output-gain', 0.65);
  const resonated = new Float32Array(frames);
  drum.process({ frames, inputs: { excitation }, outputs: { output: resonated }, controls: { energy } });

  const shaper = mod024.createInstance(context);
  shaper.setParameter('attack-gain-db', 4);
  shaper.setParameter('sustain-gain-db', -3);
  shaper.setParameter('gate-open-db', -60);
  shaper.setParameter('gate-attack-ms', 0);
  shaper.setParameter('output-gain-db', -6);
  const samples = new Float32Array(frames);
  const transient = new Float32Array(frames);
  const sustain = new Float32Array(frames);
  const gate = new Float32Array(frames);
  shaper.process({
    frames,
    inputs: { in: resonated },
    outputs: { out: samples, 'transient-envelope': transient, 'sustain-envelope': sustain, gate },
    controls: {},
  });

  let finite = true;
  let peak = 0;
  let sumSquares = 0;
  for (const sample of samples) {
    finite &&= Number.isFinite(sample);
    peak = Math.max(peak, Math.abs(sample));
    sumSquares += sample * sample;
  }
  return Object.freeze({
    audibleRoute: Object.freeze(['mir.mod-009', 'mir.mod-024'] as const),
    controlRoute: Object.freeze(['mir.mod-017', 'mir.mod-009'] as const),
    frames,
    finite,
    peak,
    rms: Math.sqrt(sumSquares / frames),
    samples,
  });
};


export interface ProcessingReferenceOptions {
  readonly morph?: number;
  readonly bitDepth?: number;
  readonly rateHz?: number;
  readonly phaserMix?: number;
  readonly bypass?: boolean;
}

export interface ProcessingReferenceResult {
  readonly audibleRoute: readonly ['mir.mod-001', 'mir.mod-020', 'mir.mod-026', 'mir.mod-029'];
  readonly frames: number;
  readonly finite: boolean;
  readonly peak: number;
  readonly rms: number;
  readonly differenceRms: number;
  readonly samples: Float32Array;
  readonly drySamples: Float32Array;
}

/** Deterministic RSS-DEV-020 example: vowel colour, rate/bit reduction and phaser. */
export const runProcessingReferenceAudio = (
  sampleRate = 48_000,
  frames = 48_000,
  options: ProcessingReferenceOptions = {},
): ProcessingReferenceResult => {
  if (!Number.isFinite(sampleRate) || sampleRate < 8_000 || sampleRate > 192_000) throw new RangeError('invalid sampleRate');
  if (!Number.isInteger(frames) || frames < 1) throw new RangeError('frames must be a positive integer');
  const morph = bounded('morph', options.morph ?? 0.55, 0, 1);
  const bitDepth = bounded('bitDepth', options.bitDepth ?? 8, 1, 24);
  if (!Number.isInteger(bitDepth)) throw new RangeError('bitDepth must be an integer');
  const rateHz = bounded('rateHz', options.rateHz ?? 12_000, 1, 192_000);
  const phaserMix = bounded('phaserMix', options.phaserMix ?? 0.6, 0, 1);
  const context = { sampleRate, maxBlockSize: frames };
  const oscillator = mod001.createInstance(context);
  const formant = mod020.createInstance(context);
  const crusher = mod026.createInstance(context);
  const phaser = mod029.createInstance(context);
  oscillator.setParameter('frequency', 98);
  oscillator.setParameter('waveform', 2);
  formant.setParameter('bank-a', 0);
  formant.setParameter('bank-b', 4);
  formant.setParameter('morph', morph);
  formant.setParameter('bandwidth', 1.15);
  formant.setParameter('output-gain', 0.65);
  crusher.setParameter('bit-depth', bitDepth);
  crusher.setParameter('rate-hz', rateHz);
  crusher.setParameter('dither', 0);
  crusher.setParameter('anti-alias-prefilter', 1);
  phaser.setParameter('center-frequency-hz', 720);
  phaser.setParameter('sweep-octaves', 1.75);
  phaser.setParameter('rate-hz', 0.4);
  phaser.setParameter('feedback', 0.25);
  phaser.setParameter('mix', phaserMix);
  phaser.setParameter('smoothing-seconds', 0);

  const source = new Float32Array(frames);
  const drySamples = new Float32Array(frames);
  const coloured = new Float32Array(frames);
  const crushed = new Float32Array(frames);
  const effected = new Float32Array(frames);
  oscillator.process({ frames, inputs: {}, outputs: { out: source }, controls: {} });
  for (let i = 0; i < frames; i += 1) drySamples[i] = source[i]! * 0.25;
  formant.process({ frames, inputs: { in: drySamples }, outputs: { out: coloured }, controls: {} });
  crusher.process({ frames, inputs: { in: coloured }, outputs: { out: crushed }, controls: {} });
  phaser.process({ frames, inputs: { in: crushed }, outputs: { out: effected }, controls: {} });

  const samples = new Float32Array(frames);
  let finite = true;
  let peak = 0;
  let sumSquares = 0;
  let differenceSquares = 0;
  for (let i = 0; i < frames; i += 1) {
    const sample = options.bypass ? drySamples[i]! : Math.max(-1, Math.min(1, effected[i]!));
    samples[i] = sample;
    finite &&= Number.isFinite(sample);
    peak = Math.max(peak, Math.abs(sample));
    sumSquares += sample * sample;
    differenceSquares += (sample - drySamples[i]!) ** 2;
  }
  return Object.freeze({
    audibleRoute: Object.freeze(['mir.mod-001', 'mir.mod-020', 'mir.mod-026', 'mir.mod-029'] as const),
    frames,
    finite,
    peak,
    rms: Math.sqrt(sumSquares / frames),
    differenceRms: Math.sqrt(differenceSquares / frames),
    samples,
    drySamples,
  });
};

export interface DelayReferenceResult { readonly audibleRoute: readonly ['mir.mod-001','mir.mod-030']; readonly finite:boolean; readonly peak:number; readonly rms:number; readonly samples:Float32Array }
/** Production QA route using the registered MOD-030 adapter and real delayed energy. */
export const runDelayReferenceAudio=(sampleRate=48_000,frames=48_000):DelayReferenceResult=>{
 if(!Number.isFinite(sampleRate)||sampleRate<8_000||sampleRate>192_000)throw new RangeError('invalid sampleRate'); if(!Number.isInteger(frames)||frames<1)throw new RangeError('frames must be a positive integer');
 const context={sampleRate,maxBlockSize:frames}, oscillator=mod001.createInstance(context), delay=mod030.createInstance(context); oscillator.setParameter('frequency',110);oscillator.setParameter('waveform',2);delay.setParameter('delay-milliseconds',125);delay.setParameter('feedback',0.5);delay.setParameter('feedback-cutoff-hz',Math.min(8000,sampleRate/2));delay.setParameter('mix',0.6);
 const source=new Float32Array(frames),rightIn=new Float32Array(frames),left=new Float32Array(frames),right=new Float32Array(frames),samples=new Float32Array(frames);oscillator.process({frames,inputs:{},outputs:{out:source},controls:{}});delay.process({frames,inputs:{'in-left':source,'in-right':rightIn},outputs:{'out-left':left,'out-right':right},controls:{}});let finite=true,peak=0,sum=0;for(let i=0;i<frames;i++){const v=(left[i]!+right[i]!)*0.5;samples[i]=v;finite&&=Number.isFinite(v);peak=Math.max(peak,Math.abs(v));sum+=v*v}return Object.freeze({audibleRoute:Object.freeze(['mir.mod-001','mir.mod-030'] as const),finite,peak,rms:Math.sqrt(sum/frames),samples});
};

export interface ModulationReferenceResult {
  readonly audibleRoute: readonly ['mir.mod-001', 'mir.mod-028'];
  readonly finite: boolean;
  readonly peak: number;
  readonly rms: number;
  readonly differenceRms: number;
  readonly samples: Float32Array;
}

/** Production QA route using the registered MOD-028 adapter and audible wet modulation. */
export const runModulationReferenceAudio = (sampleRate = 48_000, frames = 48_000): ModulationReferenceResult => {
  if (!Number.isFinite(sampleRate) || sampleRate < 8_000 || sampleRate > 192_000) throw new RangeError('invalid sampleRate');
  if (!Number.isInteger(frames) || frames < 1) throw new RangeError('frames must be a positive integer');
  const context = { sampleRate, maxBlockSize: frames };
  const oscillator = mod001.createInstance(context);
  const modulation = mod028.createInstance(context);
  oscillator.setParameter('frequency', 110);
  oscillator.setParameter('waveform', 2);
  modulation.setParameter('mode', 2);
  modulation.setParameter('rate-hz', 0.7);
  modulation.setParameter('depth', 0.85);
  modulation.setParameter('width', 0.8);
  modulation.setParameter('feedback', 0.35);
  modulation.setParameter('mix', 0.65);
  const source = new Float32Array(frames);
  const left = new Float32Array(frames);
  const right = new Float32Array(frames);
  const samples = new Float32Array(frames);
  oscillator.process({ frames, inputs: {}, outputs: { out: source }, controls: {} });
  modulation.process({ frames, inputs: { 'in-left': source, 'in-right': source }, outputs: { 'out-left': left, 'out-right': right }, controls: {} });
  let finite = true; let peak = 0; let sum = 0; let difference = 0;
  for (let i = 0; i < frames; i += 1) {
    const sample = (left[i]! + right[i]!) * 0.5;
    samples[i] = sample;
    finite &&= Number.isFinite(sample);
    peak = Math.max(peak, Math.abs(sample));
    sum += sample * sample;
    difference += (sample - source[i]!) ** 2;
  }
  return Object.freeze({ audibleRoute: Object.freeze(['mir.mod-001', 'mir.mod-028'] as const), finite, peak, rms: Math.sqrt(sum / frames), differenceRms: Math.sqrt(difference / frames), samples });
};


export interface ReverbReferenceResult { readonly audibleRoute:readonly ['mir.mod-001','mir.mod-031']; readonly finite:boolean; readonly peak:number; readonly rms:number; readonly differenceRms:number; readonly samples:Float32Array }
/** Production QA route through the registered MOD-031 FDN. */
export const runReverbReferenceAudio=(sampleRate=48_000,frames=48_000):ReverbReferenceResult=>{
 if(!Number.isFinite(sampleRate)||sampleRate<8_000||sampleRate>192_000)throw new RangeError('invalid sampleRate');if(!Number.isInteger(frames)||frames<1)throw new RangeError('frames must be a positive integer');const context={sampleRate,maxBlockSize:frames},osc=mod001.createInstance(context),reverb=mod031.createInstance(context);osc.setParameter('frequency',110);osc.setParameter('waveform',2);reverb.setParameter('mode',3);reverb.setParameter('room-size',.75);reverb.setParameter('decay-seconds',2.4);reverb.setParameter('damping',.3);reverb.setParameter('diffusion',.8);reverb.setParameter('mix',.65);const source=new Float32Array(frames),left=new Float32Array(frames),right=new Float32Array(frames),samples=new Float32Array(frames);osc.process({frames,inputs:{},outputs:{out:source},controls:{}});reverb.process({frames,inputs:{'in-left':source,'in-right':source},outputs:{'out-left':left,'out-right':right},controls:{}});let finite=true,peak=0,sum=0,diff=0;for(let i=0;i<frames;i++){const v=(left[i]!+right[i]!)*.5;samples[i]=v;finite&&=Number.isFinite(v);peak=Math.max(peak,Math.abs(v));sum+=v*v;diff+=(v-source[i]!)**2}return Object.freeze({audibleRoute:Object.freeze(['mir.mod-001','mir.mod-031']as const),finite,peak,rms:Math.sqrt(sum/frames),differenceRms:Math.sqrt(diff/frames),samples});
};

export function runMixerQaRoute(sampleRate = 48_000) {
 const frames = 1024, mixer = new MixerRoutingGraph(sampleRate, frames, 2, 2), oscillator=mod001.createInstance({sampleRate,maxBlockSize:frames});
 const a=mixer.startVoice('osc-a',1,0).slot,b=mixer.startVoice('osc-b',2,0).slot;mixer.configureVoice(a,{gain:.8,sendBus:1,sendGain:.35});mixer.configureVoice(b,{gain:.35,bus:1});mixer.configureEdges([{from:0,to:1,gain:.2}]);
 const left=new Float32Array(frames),right=new Float32Array(frames);oscillator.setParameter('frequency',110);oscillator.setParameter('waveform',2);oscillator.process({frames,inputs:{},outputs:{out:left},controls:{}});oscillator.reset();oscillator.setParameter('frequency',220);oscillator.setParameter('waveform',1);oscillator.process({frames,inputs:{},outputs:{out:right},controls:{}});
 const buses=[new Float32Array(frames),new Float32Array(frames)],output=new Float32Array(frames);mixer.processInto([left,right],buses,output,frames);
 let peak=0,energy=0,delta=0;for(let i=0;i<frames;i++){const y=output[i]!,x=left[i]!;peak=Math.max(peak,Math.abs(y));energy+=y*y;delta+=Math.abs(y-x)}return Object.freeze({route:Object.freeze(['mir.mod-001','mir.mod-034']as const),finite:output.every(Number.isFinite),nonZero:energy>1e-6,affected:delta>1e-3,bounded:peak<=4,peak,rms:Math.sqrt(energy/frames)});
}


export function runSpatializerQaRoute(sampleRate = 48_000) {
 const frames=4096,oscillator=mod001.createInstance({sampleRate,maxBlockSize:frames}),spatializer=new SpatializerProcessor({sampleRate,maxBlockSize:frames,objectCapacity:1,width:1.25,subMonoCutoffHz:90,trajectorySeconds:0});
 const source=new Float32Array(frames),left=new Float32Array(frames),right=new Float32Array(frames);oscillator.setParameter('frequency',220);oscillator.setParameter('waveform',2);oscillator.process({frames,inputs:{},outputs:{out:source},controls:{}});spatializer.configureObject(0,{pan:.65,distance:.08,gain:.8});spatializer.processInto([source],left,right,frames);
 let peak=0,energy=0,side=0,difference=0,finite=true;for(let i=0;i<frames;i++){const l=left[i]!,r=right[i]!;finite&&=Number.isFinite(l)&&Number.isFinite(r);peak=Math.max(peak,Math.abs(l),Math.abs(r));energy+=l*l+r*r;side+=(l-r)*(l-r);difference+=(l-source[i]!)**2+(r-source[i]!)**2}return Object.freeze({route:Object.freeze(['mir.mod-001','mir.mod-033']as const),finite,nonZero:energy>1e-6,affected:difference>1e-4,bounded:peak<=4,peak,rms:Math.sqrt(energy/(frames*2)),sideRms:Math.sqrt(side/frames)});
}

export function runAdditiveQaRoute(sampleRate = 48_000) {
  const frames = 4096;
  const additive = new AdditivePartialBank({ sampleRate, maxBlockSize: frames, bank: 3, fundamental: 110, spectralTiltDbPerOctave: -3, level: 0.8 });
  const samples = new Float32Array(frames); additive.processInto(samples);
  const reference = new AdditivePartialBank({ sampleRate, maxBlockSize: frames, bank: 0, fundamental: 110, spectralTiltDbPerOctave: -3, level: 0.8 });
  const harmonic = new Float32Array(frames); reference.processInto(harmonic);
  let peak = 0, energy = 0, difference = 0, finite = true;
  for (let i = 0; i < frames; i++) { const value = samples[i]!; finite &&= Number.isFinite(value); peak = Math.max(peak, Math.abs(value)); energy += value * value; difference += (value - harmonic[i]!) ** 2; }
  return Object.freeze({ route: Object.freeze(['mir.mod-002'] as const), finite, nonZero: energy > 1e-6, affected: difference > 1e-4, bounded: peak <= 1, peak, rms: Math.sqrt(energy / frames), samples });
}

export function runCombQaRoute(sampleRate = 48_000) {
  const frames = 4096, oscillator = mod001.createInstance({ sampleRate, maxBlockSize: frames });
  const source = new Float32Array(frames), samples = new Float32Array(frames); oscillator.setParameter('frequency', 220); oscillator.setParameter('waveform', 2); oscillator.process({ frames, inputs: {}, outputs: { out: source }, controls: {} });
  const comb = new CombFilter({ sampleRate, maxBlockSize: frames, mode: 'feedback', delayMs: 7.3, gain: 0.72, damping: 0.25 }); comb.processInto(source, samples, frames);
  let finite = true, peak = 0, energy = 0, difference = 0; for (let i = 0; i < frames; i++) { const y = samples[i]!; finite &&= Number.isFinite(y); peak = Math.max(peak, Math.abs(y)); energy += y * y; difference += (y - source[i]!) ** 2; }
  return Object.freeze({ route: Object.freeze(['mir.mod-001', 'mir.mod-022'] as const), finite, nonZero: energy > 1e-6, affected: difference > 1e-4, bounded: peak <= 1, peak, rms: Math.sqrt(energy / frames), samples });
}


export interface ResamplerQaReadback { readonly samples: Float32Array; readonly frames: number; readonly peak: number; readonly rms: number; readonly differenceRms: number; readonly finite: boolean; readonly nonZero: boolean; readonly bounded: boolean; readonly affected: boolean; readonly audibleRoute: readonly string[]; }
export const runResamplerQaRoute = (sampleRate = 48_000, frames = 4096): ResamplerQaReadback => {
  if (!Number.isInteger(frames) || frames < 128 || frames > sampleRate * 10) throw new RangeError('frames outside resampler QA bounds');
  const source = Float32Array.from({ length: frames }, (_, i) => 0.72 * Math.sin(2 * Math.PI * 330 * i / sampleRate) + 0.18 * Math.sin(2 * Math.PI * 3300 * i / sampleRate));
  const bank = new ResamplerBank(8); bank.load(0, source); bank.configure(0, { rate: 0.731, pitchCents: 0, start: 0, end: frames, loop: true, quality: 'sinc' });
  const samples = new Float32Array(frames); bank.render(0, samples); let peak = 0, energy = 0, difference = 0;
  for (let i = 0; i < frames; i++) { const value = samples[i]!; peak = Math.max(peak, Math.abs(value)); energy += value * value; const delta = value - source[i]!; difference += delta * delta; }
  const rms = Math.sqrt(energy / frames), differenceRms = Math.sqrt(difference / frames), finite = samples.every(Number.isFinite);
  return Object.freeze({ samples, frames, peak, rms, differenceRms, finite, nonZero: rms > 0.001, bounded: peak <= 1, affected: differenceRms > 0.001, audibleRoute: Object.freeze(['mir.mod-027']) });
};


export interface SelfBufferQaReadback { readonly left: Float32Array; readonly right: Float32Array; readonly frames:number; readonly peak:number; readonly rms:number; readonly differenceRms:number; readonly finite:boolean; readonly nonZero:boolean; readonly bounded:boolean; readonly affected:boolean; readonly channelParity:boolean; readonly audibleRoute:readonly string[]; }
export const runSelfBufferQaRoute=(sampleRate=48_000,frames=4096):SelfBufferQaReadback=>{if(!Number.isInteger(frames)||frames<128||frames>sampleRate*10)throw new RangeError('frames outside self-buffer QA bounds');const sourceL=Float32Array.from({length:frames},(_,i)=>.62*Math.sin(2*Math.PI*247*i/sampleRate)),sourceR=Float32Array.from({length:frames},(_,i)=>.47*Math.sin(2*Math.PI*509*i/sampleRate));const bank=new SelfBufferBank(sampleRate);bank.beginRecord(0,2,frames);bank.recordInto(0,[sourceL,sourceR],frames);bank.freeze(0);bank.configure(0,{rate:-.731,loop:true,seamFrames:Math.min(256,Math.floor(frames/2)),gain:.8});const left=new Float32Array(frames),right=new Float32Array(frames);bank.render(0,left,right);let peak=0,energy=0,difference=0,stereoDifference=0;for(let i=0;i<frames;i++){peak=Math.max(peak,Math.abs(left[i]!),Math.abs(right[i]!));energy+=left[i]!*left[i]!+right[i]!*right[i]!;const d=left[i]!-sourceL[i]!;difference+=d*d;const sd=left[i]!-right[i]!;stereoDifference+=sd*sd;}const rms=Math.sqrt(energy/(frames*2)),differenceRms=Math.sqrt(difference/frames),finite=left.every(Number.isFinite)&&right.every(Number.isFinite);return Object.freeze({left,right,frames,peak,rms,differenceRms,finite,nonZero:rms>.001,bounded:peak<=.620001,affected:differenceRms>.001,channelParity:stereoDifference>.001,audibleRoute:Object.freeze(['mir.mod-007'])});};


export interface StringQaReadback { readonly left: Float32Array; readonly right: Float32Array; readonly frames:number; readonly peak:number; readonly rms:number; readonly finite:boolean; readonly nonZero:boolean; readonly bounded:boolean; readonly stereo:boolean; readonly audibleRoute:readonly string[]; }
export const runStringQaRoute=(sampleRate=48_000,frames=4096):StringQaReadback=>{if(!Number.isInteger(frames)||frames<128||frames>sampleRate*10)throw new RangeError('frames outside string QA bounds');const resonator=new StringWaveguideResonator({sampleRate,maxBlockSize:Math.min(frames,16_384)});resonator.setVoiceParameters(3,{decaySeconds:4,damping:.22,dispersion:.4,gain:.85});resonator.setVoiceParameters(12,{decaySeconds:3,damping:.38,dispersion:.7,gain:.7});resonator.noteOn(3,146.83,.9,20260909);resonator.noteOn(12,220,.72,20260910);const left=new Float32Array(frames),right=new Float32Array(frames);for(let offset=0;offset<frames;offset+=resonator.maxBlockSize){const count=Math.min(resonator.maxBlockSize,frames-offset);resonator.processInto(left.subarray(offset,offset+count),right.subarray(offset,offset+count),count);}let peak=0,energy=0,side=0;for(let i=0;i<frames;i++){peak=Math.max(peak,Math.abs(left[i]!),Math.abs(right[i]!));energy+=left[i]!*left[i]!+right[i]!*right[i]!;side+=(left[i]!-right[i]!)**2;}const rms=Math.sqrt(energy/(frames*2)),finite=left.every(Number.isFinite)&&right.every(Number.isFinite);return Object.freeze({left,right,frames,peak,rms,finite,nonZero:rms>.001,bounded:peak<=.95,stereo:side>1e-6,audibleRoute:Object.freeze(['mir.mod-010'])});};


export interface BoreQaReadback {
  readonly left: Float32Array;
  readonly right: Float32Array;
  readonly frames: number;
  readonly peak: number;
  readonly rms: number;
  readonly finite: boolean;
  readonly nonZero: boolean;
  readonly bounded: boolean;
  readonly stereo: boolean;
  readonly audibleRoute: readonly ['mir.mod-011'];
}

/** Deterministic production smoke using both physical bore models. */
export const runBoreQaRoute = (sampleRate = 48_000, frames = 4096): BoreQaReadback => {
  if (!Number.isInteger(frames) || frames < 128 || frames > sampleRate * 10) throw new RangeError('frames outside bore QA bounds');
  const resonator = new TubeReedLipResonator({ sampleRate, maxBlockSize: Math.min(frames, 16_384) });
  for (let voice = 0; voice < 8; voice += 1) {
    resonator.setVoiceParameters(voice, { model: voice % 2 === 0 ? 'reed' : 'lip', boreLoss: 0.16 + voice * 0.01, brightness: 0.72, nonlinearity: 0.62, gain: 1 });
    resonator.noteOn(voice, 110 + voice * 27.5, 1, 20260911 + voice);
  }
  const left = new Float32Array(frames);
  const right = new Float32Array(frames);
  for (let offset = 0; offset < frames; offset += resonator.maxBlockSize) {
    const count = Math.min(resonator.maxBlockSize, frames - offset);
    resonator.processInto(left.subarray(offset, offset + count), right.subarray(offset, offset + count), count);
  }
  let peak = 0;
  let energy = 0;
  let side = 0;
  for (let index = 0; index < frames; index += 1) {
    peak = Math.max(peak, Math.abs(left[index]!), Math.abs(right[index]!));
    energy += left[index]! ** 2 + right[index]! ** 2;
    side += (left[index]! - right[index]!) ** 2;
  }
  const rms = Math.sqrt(energy / (frames * 2));
  const finite = left.every(Number.isFinite) && right.every(Number.isFinite);
  return Object.freeze({ left, right, frames, peak, rms, finite, nonZero: rms > 0.001, bounded: peak <= 0.95, stereo: side > 1e-6, audibleRoute: Object.freeze(['mir.mod-011'] as const) });
};


export interface GranularQaReadback {
  readonly left: Float32Array; readonly right: Float32Array; readonly frames: number;
  readonly peak: number; readonly rms: number; readonly finite: boolean; readonly nonZero: boolean;
  readonly bounded: boolean; readonly stereo: boolean; readonly activeGrains: number;
  readonly audibleRoute: readonly ['mir.mod-013'];
}

/** Deterministic production smoke of the immutable-source granular cloud. */
export const runGranularQaRoute = (sampleRate = 48_000, frames = 4096): GranularQaReadback => {
  if (!Number.isInteger(frames) || frames < 128 || frames > Math.min(sampleRate * 10, 8192)) throw new RangeError('frames outside granular QA bounds');
  const sourceFrames = Math.max(2048, Math.round(sampleRate * .25));
  const source = Float32Array.from({ length: sourceFrames }, (_, index) => {
    const phase = 2 * Math.PI * index / sampleRate;
    return .62 * Math.sin(phase * 137) + .25 * Math.sin(phase * 311) + .1 * Math.sin(phase * 719);
  });
  const engine = new GranularEngine({ sampleRate, maxBlockSize: frames });
  engine.loadSource(source);
  engine.setParameters({ position: .5, positionSpread: .7, playbackRate: .83, pitchCents: 173, durationMs: 42, density: 420, durationJitter: .35, rateJitter: .18, pitchJitter: .12, stereoSpread: .9, gain: .8, loop: true, seed: 20260913 });
  const left = new Float32Array(frames), right = new Float32Array(frames);
  engine.processInto(left, right, frames);
  let peak=0, energy=0, side=0;
  for(let index=0;index<frames;index++){ peak=Math.max(peak,Math.abs(left[index]!),Math.abs(right[index]!)); energy+=left[index]!**2+right[index]!**2; side+=(left[index]!-right[index]!)**2; }
  const rms=Math.sqrt(energy/(frames*2)), finite=left.every(Number.isFinite)&&right.every(Number.isFinite);
  return Object.freeze({ left,right,frames,peak,rms,finite,nonZero:rms>.001,bounded:peak<=1,stereo:side>1e-6,activeGrains:engine.getState().activeGrains,audibleRoute:Object.freeze(['mir.mod-013'] as const) });
};

export interface ConvolutionQaReadback {
  readonly left: Float32Array; readonly right: Float32Array; readonly frames: number;
  readonly peak: number; readonly rms: number; readonly differenceRms: number;
  readonly finite: boolean; readonly nonZero: boolean; readonly bounded: boolean;
  readonly stereo: boolean; readonly affected: boolean; readonly latencyFrames: number;
  readonly provenanceId: string; readonly sourceHash: string;
  readonly audibleRoute: readonly ['mir.mod-032'];
}

/** Deterministic production smoke of the generated-room partitioned convolution path. */
export const runConvolutionQaRoute = (sampleRate = 48_000, frames = 4096): ConvolutionQaReadback => {
  if (!Number.isInteger(frames) || frames < 256 || frames > Math.min(sampleRate * 10, 8192)) throw new RangeError('frames outside convolution QA bounds');
  const processor = new ConvolutionBodyIrProcessor({ sampleRate, maxBlockSize: frames, partitionSize: 128 });
  const request = generateProceduralIr({ family: 'room', sampleRate, durationMs: 42, decaySeconds: .38, damping: .62, stereoWidth: .9, seed: 20260914, normalization: 'peak' });
  processor.loadIr(0, request);
  processor.setControls(0, { wet: .72, dry: .12, outputGain: .82, bypass: false });
  const inputLeft = Float32Array.from({ length: frames }, (_, index) => .31 * Math.sin(2 * Math.PI * 173 * index / sampleRate));
  const inputRight = Float32Array.from({ length: frames }, (_, index) => .27 * Math.sin(2 * Math.PI * 281 * index / sampleRate));
  inputLeft[0] = .7; inputRight[0] = .5;
  const left = new Float32Array(frames), right = new Float32Array(frames);
  processor.processInto(0, inputLeft, inputRight, left, right, frames);
  let peak = 0, energy = 0, difference = 0, side = 0;
  for (let index = 0; index < frames; index += 1) {
    peak = Math.max(peak, Math.abs(left[index]!), Math.abs(right[index]!));
    energy += left[index]! ** 2 + right[index]! ** 2;
    difference += (left[index]! - inputLeft[index]!) ** 2 + (right[index]! - inputRight[index]!) ** 2;
    side += (left[index]! - right[index]!) ** 2;
  }
  const rms = Math.sqrt(energy / (frames * 2));
  const differenceRms = Math.sqrt(difference / (frames * 2));
  const readback = processor.readback(0);
  return Object.freeze({ left, right, frames, peak, rms, differenceRms,
    finite: left.every(Number.isFinite) && right.every(Number.isFinite), nonZero: rms > .001,
    bounded: peak <= 1, stereo: side > 1e-6, affected: differenceRms > .001,
    latencyFrames: readback.latencyFrames, provenanceId: readback.provenance!.id,
    sourceHash: readback.provenance!.sourceHash, audibleRoute: Object.freeze(['mir.mod-032'] as const) });
};

export interface FrictionQaReadback {
  readonly left: Float32Array;
  readonly right: Float32Array;
  readonly frames: number;
  readonly peak: number;
  readonly rms: number;
  readonly finite: boolean;
  readonly nonZero: boolean;
  readonly bounded: boolean;
  readonly stereo: boolean;
  readonly audibleRoute: readonly ['mir.mod-012'];
}

/** Deterministic production smoke across distinct friction materials and pans. */
export const runFrictionQaRoute = (sampleRate = 48_000, frames = 4096): FrictionQaReadback => {
  if (!Number.isInteger(frames) || frames < 128 || frames > Math.min(sampleRate * 10, 8192)) throw new RangeError('frames outside friction QA bounds');
  const exciter = new FrictionBowExciter({ sampleRate, maxBlockSize: frames });
  exciter.setContactParameters(0, { active: true, material: 'bow', pressure: 0.82, speed: 0.57, roughness: 0.34, level: 0.72, pan: -0.55, seed: 20260912 });
  exciter.setContactParameters(1, { active: true, material: 'metal', pressure: 0.64, speed: -0.73, roughness: 0.71, level: 0.56, pan: 0.62, seed: 20260913 });
  const left = new Float32Array(frames);
  const right = new Float32Array(frames);
  exciter.processInto(left, right, frames);
  let peak = 0;
  let energy = 0;
  let side = 0;
  for (let index = 0; index < frames; index += 1) {
    peak = Math.max(peak, Math.abs(left[index]!), Math.abs(right[index]!));
    energy += left[index]! ** 2 + right[index]! ** 2;
    side += (left[index]! - right[index]!) ** 2;
  }
  const rms = Math.sqrt(energy / (frames * 2));
  const finite = left.every(Number.isFinite) && right.every(Number.isFinite);
  return Object.freeze({ left, right, frames, peak, rms, finite, nonZero: rms > 0.001, bounded: peak <= 2, stereo: side > 1e-6, audibleRoute: Object.freeze(['mir.mod-012'] as const) });
};
